import { TRPCErrorBoundary } from '@client/components/_core/error-handling/TRPCErrorBoundary';
import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { usePathState } from '@client/hooks/navigation/usePathState';
import { createColumnHelper, PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { type RouterInputs, type RouterOutputs, trpc } from '@client/trpc/client';
import { DEFAULT_PAGE_SIZE } from '@shared/constants';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { Dropdown } from 'antd';
import { ChevronDown, FileText, Package } from 'lucide-react';

export const Route = createFileRoute('/console/$orgId/configuration/claims-products/products-v2')({
    staticData: {
        metadata: {
            title: 'Products',
        },
        ui: {
            icon: Package,
            title: 'Products',
            subtitle: 'Manage products and their versions',
            useTitleForChildren: true,
            menuDescription: 'Manage products and their versions',
        },
        presentation: 'fullPage',
        modules: ['claim'],
        permissions: ['settings:update'],
        featureFlag: 'is-products-v2-enabled',
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <ProductsV2Search />
        </PageController>
    );
}

type QueryParams = RouterInputs['product']['listProducts'];

const columnHelper = createColumnHelper<
    RouterOutputs['product']['listProducts']['rows'][number],
    QueryParams['orderBy']
>();

function ProductsV2Search() {
    const orgId = useOrgId();
    const navigate = useNavigate();

    const [input, setInput] = usePathState<QueryParams>('query-params', {
        orderBy: { id: 'desc' },
        limit: DEFAULT_PAGE_SIZE,
        offset: 0,
    });

    const { data, isPending, error, refetch } = trpc.product.listProducts.useQuery(input);

    const tableProps = usePrismaTable({
        rowKey: 'id',
        data: data?.rows,
        total: data?.total,
        loading: isPending,
        queryParams: input,
        onParamsChange: setInput,
        columnVisibility: {
            enabled: true,
            persistKey: 'products-v2-table-columns',
            enableReordering: true,
        },
        refreshable: {
            enabled: true,
            onRefresh: refetch,
        },
        columns: [
            columnHelper.accessor('name', {
                id: 'name',
                header: 'Name',
                sortable: 'name',
            }),
            columnHelper.accessor('type', {
                id: 'type',
                header: 'Type',
                sortable: 'type',
            }),
            columnHelper.display({
                id: 'insurer',
                header: 'Insurer',
                cell({ row }) {
                    return row.Insurer?.name ?? '—';
                },
            }),
            columnHelper.actions({
                width: 200,
                cell({ row }) {
                    return (
                        <div onClick={(e) => e.stopPropagation()}>
                            <Button.Edit.Link
                                to="/console/$orgId/configuration/claims-products/products-v2/edit/$productId"
                                params={{
                                    orgId,
                                    productId: row.id,
                                }}
                            />
                            <Dropdown
                                trigger={['click']}
                                menu={{
                                    items: [
                                        {
                                            key: 'versions',
                                            label: 'Versions',
                                            icon: <FileText size={14} className="text-blue-600" />,
                                            disabled: !row.lastProductVersionId,
                                            onClick() {
                                                if (!row.lastProductVersionId) {
                                                    return;
                                                }

                                                navigate({
                                                    to: '/console/$orgId/configuration/claims-products/products-v2/details/$productId/product-versions/details/$productVersionId/details',
                                                    params: {
                                                        orgId,
                                                        productId: row.id,
                                                        productVersionId: row.lastProductVersionId,
                                                    },
                                                });
                                            },
                                        },
                                    ],
                                }}
                            >
                                <Button type="tertiary" icon={<ChevronDown />} iconPosition="end">
                                    Configure
                                </Button>
                            </Dropdown>
                        </div>
                    );
                },
            }),
        ],
    });

    return (
        <TRPCErrorBoundary error={error}>
            <Outlet />

            <TableToolbar
                actions={
                    <Button.Add.Link
                        to="/console/$orgId/configuration/claims-products/products-v2/add"
                        params={{
                            orgId,
                        }}
                        variant="primary"
                        label="New product"
                    />
                }
            />
            <PrismaTable
                {...tableProps}
                onRow={({ id }) => ({
                    className: 'cursor-pointer',
                    onClick(e) {
                        navigate(
                            {
                                to: '/console/$orgId/configuration/claims-products/products-v2/edit/$productId',
                                params: {
                                    orgId,
                                    productId: id,
                                },
                            },
                            e,
                        );
                    },
                })}
            />
        </TRPCErrorBoundary>
    );
}
